/** Thin promise wrappers around the Hive Keychain browser extension. */

export interface KeychainResponse {
  success: boolean;
  error?: string | null;
  message?: string;
  result?: any;
  data?: any;
  publicKey?: string;
}

export interface HiveKeychain {
  requestHandshake(cb: () => void): void;
  requestSignBuffer(account: string | null, message: string, key: 'Posting' | 'Active' | 'Memo', cb: (r: KeychainResponse) => void, rpc?: string, title?: string): void;
  requestTransfer(account: string, to: string, amount: string, memo: string, currency: 'HIVE' | 'HBD', cb: (r: KeychainResponse) => void, enforce?: boolean): void;
  requestSendToken(account: string, to: string, amount: string, memo: string, currency: string, cb: (r: KeychainResponse) => void): void;
  requestCustomJson(account: string | null, id: string, key: 'Posting' | 'Active', json: string, display: string, cb: (r: KeychainResponse) => void): void;
}

export function isKeychainAvailable(): boolean {
  return typeof window !== 'undefined' && !!window.hive_keychain;
}

/** Keychain injects itself slightly after load; wait briefly before deciding it's absent. */
export function waitForKeychain(timeoutMs = 1500): Promise<boolean> {
  return new Promise((resolve) => {
    if (isKeychainAvailable()) return resolve(true);
    const start = Date.now();
    const t = setInterval(() => {
      if (isKeychainAvailable()) { clearInterval(t); resolve(true); }
      else if (Date.now() - start > timeoutMs) { clearInterval(t); resolve(false); }
    }, 100);
  });
}

function kc(): HiveKeychain {
  if (!isKeychainAvailable()) throw new Error('Hive Keychain is not installed');
  return window.hive_keychain!;
}

export function signBuffer(account: string, message: string, key: 'Posting' | 'Active' = 'Posting'): Promise<KeychainResponse> {
  return new Promise((resolve) => kc().requestSignBuffer(account, message, key, resolve, undefined, 'Sign in to Bingo Dash'));
}

export function transferHive(account: string, to: string, amount: string, memo: string, currency: 'HIVE' | 'HBD' = 'HIVE'): Promise<KeychainResponse> {
  return new Promise((resolve) => kc().requestTransfer(account, to, amount, memo, currency, resolve, true));
}

export function sendToken(account: string, to: string, amount: string, memo: string, symbol: string): Promise<KeychainResponse> {
  return new Promise((resolve) => kc().requestSendToken(account, to, amount, memo, symbol, resolve));
}
